import {
  CommentModel,
  CommentSearchResultModel,
  CommentStatus,
  CreateCommentRequest,
  ReactionModel,
  UpdateCommentRequest,
} from '../types/CommentModel';
import { assertResponse, getJson } from './Utils';

const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

// Flat list of comments for a game; the client builds the 2-level tree by ParentId.
async function getThread(gameId: number): Promise<CommentModel[]> {
  const response = await fetch('/api/Comments?gameId=' + gameId);
  return await getJson<CommentModel[]>(response);
}

// Reads the response body first so a 400 with a validation message can surface it.
async function readCommentOrThrow(response: Response): Promise<CommentModel> {
  const text = await response.text();
  if (!response.ok) {
    throw new Error(
      text && text.trim() !== '' ? text : `${response.status} ${response.statusText}`,
    );
  }
  return JSON.parse(text) as CommentModel;
}

async function create(request: CreateCommentRequest): Promise<CommentModel> {
  const response = await fetch('/api/Comments', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(request),
  });
  return await readCommentOrThrow(response);
}

async function update(id: number, request: UpdateCommentRequest): Promise<CommentModel> {
  const response = await fetch('/api/Comments/' + id, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(request),
  });
  return await readCommentOrThrow(response);
}

async function remove(id: number): Promise<void> {
  const response = await fetch('/api/Comments/' + id, {
    method: 'DELETE',
    headers: jsonHeaders,
  });
  await assertResponse(response);
}

async function moderate(id: number, status: CommentStatus): Promise<void> {
  const response = await fetch('/api/Comments/' + id + '/moderate', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ Status: status }),
  });
  await assertResponse(response);
}

// Toggle the current user's reaction with `emoji`; returns the comment's updated per-emoji tallies.
async function toggleReaction(id: number, emoji: string): Promise<ReactionModel[]> {
  const response = await fetch('/api/Comments/' + id + '/reactions', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ Emoji: emoji }),
  });
  return await getJson<ReactionModel[]>(response);
}

async function search(q: string, take = 20): Promise<CommentSearchResultModel[]> {
  const url = '/api/Comments/search?q=' + encodeURIComponent(q) + '&take=' + take;
  const response = await fetch(url);
  return await getJson<CommentSearchResultModel[]>(response);
}

export default {
  getThread,
  create,
  update,
  remove,
  moderate,
  toggleReaction,
  search,
};
